import React, { useEffect, useState } from 'react';
import { Antenna, Loader2, RefreshCw, Save, Terminal, Lock, Radio, Play, Download, Upload, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { settingsAPI, oltV2API } from '../services/api';
import useDarkReady from '../theme/useDarkReady';

const MASKED = '••••••••';

const FIELD = ({ label, hint, children }) => (
  <div className="form-group">
    <label className="form-label">{label}</label>
    {children}
    {hint && <div className="form-hint">{hint}</div>}
  </div>
);

// Mirrors oltService.js's own fetch/push list, one entry per function exported from
// oltServiceV2.js. `fields` drives a tiny inline form per card; `run` maps those values onto
// the matching oltV2API call. Commands themselves are still TODO_CONFIRM placeholders in
// oltServiceV2.js's CLI table — these buttons call the CLI transport picked below (Telnet or
// SSH), but what that path sends to the OLT isn't verified syntax yet (see that file's CLI
// table comment).
const FETCH_ACTIONS = [
  { key: 'onus',      label: 'Fetch ONUs',         fields: [],                                            run: () => oltV2API.getONUs() },
  { key: 'onuDetail', label: 'Fetch ONU Detail',   fields: ['port', 'onuId'],                              run: v => oltV2API.getONUDetail(v.port, v.onuId) },
  { key: 'ports',     label: 'Fetch PON Ports',    fields: [],                                             run: () => oltV2API.getPorts() },
  { key: 'alarms',    label: 'Fetch Alarms',       fields: [],                                             run: () => oltV2API.getAlarms() },
  { key: 'gePorts',   label: 'Fetch GE Ports',     fields: [],                                             run: () => oltV2API.getGEPorts() },
];
const PUSH_ACTIONS = [
  { key: 'provision',   label: 'Provision ONU',        fields: ['port', 'onuId', 'serialNumber', 'lineProfile', 'serviceProfile'], run: v => oltV2API.provisionONU(v) },
  { key: 'deregister',  label: 'Deregister ONU',       fields: ['port', 'onuId'],                                                  run: v => oltV2API.deregisterONU(v.port, v.onuId) },
  { key: 'reboot',      label: 'Reboot ONU',           fields: ['port', 'onuId'],                                                  run: v => oltV2API.rebootONU(v.port, v.onuId) },
  { key: 'upgrade',     label: 'Upgrade ONU Firmware', fields: ['port', 'onuId', 'firmwareVersion'],                               run: v => oltV2API.upgradeONU(v.port, v.onuId, v.firmwareVersion) },
  { key: 'setState',    label: 'Set ONU Admin State',  fields: ['port', 'onuId', 'state'],                                         run: v => oltV2API.setONUAdminState(v.port, v.onuId, v.state) },
  { key: 'saveLabel',   label: 'Save ONU Label',       fields: ['port', 'onuId', 'label'],                                         run: v => oltV2API.saveLabel(v.port, v.onuId, v.label) },
  { key: 'changeProfile', label: 'Change ONU Profile', fields: ['port', 'onuId', 'lineProfile', 'serviceProfile'],                 run: v => oltV2API.changeONUProfile(v.port, v.onuId, v.lineProfile, v.serviceProfile) },
];

function ActionCard({ action, values, setValues, running, onRun, result, danger }) {
  return (
    <div className="stat-card" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontWeight: 700, fontSize: '.88rem' }}>{action.label}</div>
      {action.fields.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {action.fields.map(f => (
            <input key={f} className="form-input" style={{ width: 120, fontSize: '.78rem', padding: '4px 8px' }}
              placeholder={f} value={values[f] || ''}
              onChange={e => setValues(action.key, f, e.target.value)} />
          ))}
        </div>
      )}
      <button type="button" className={`btn btn-sm ${danger ? 'btn-danger' : 'btn-secondary'}`} disabled={running}
        onClick={onRun}>
        {running ? <Loader2 size={12} className="spin" style={{ marginRight: 5 }} /> : <Play size={12} style={{ marginRight: 5 }} />}
        Run
      </button>
      {result && (
        <pre style={{
          fontSize: '.72rem', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          color: result.ok ? 'var(--text)' : 'var(--danger)', maxHeight: 140, overflow: 'auto',
          background: 'var(--bg-secondary, #f8fafc)', padding: 8, borderRadius: 6,
        }}>
          {result.ok ? JSON.stringify(result.data, null, 2) : result.message}
        </pre>
      )}
    </div>
  );
}

// Independent from OLTPage.js / Settings > OLT Connection — that page talks to the OLT over
// its management IP (HTTP web-UI scraping). This page has no HTTP path at all: it's pure CLI
// automation over Telnet or SSH, picked by `cliTransport` below (both credentials are stored
// and independently testable regardless of which one is active). Own device pointer (`ip`),
// own session state, own request mutex (services/oltServiceV2.js, models/OLTConfigV2.js) — so
// experimenting here, on either transport, can't destabilize the production integration.
export default function OltInfoV2Page() {
  useDarkReady();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    ip: '', cliTransport: 'telnet', isEnabled: false,
    telnet: { port: 23, username: 'admin', password: '' },
    ssh: { port: 22, username: 'admin', password: '' },
    snmp: { port: 161, version: '2c', community: '' },
  });

  const [testing, setTesting] = useState(null); // 'telnet' | 'ssh' | 'snmp' | null
  const [results, setResults] = useState({}); // { telnet: {...}, ssh: {...}, snmp: {...} }

  // ── Raw CLI runner state ──
  const [cliCmd, setCliCmd] = useState('');
  const [cliRunning, setCliRunning] = useState(false);
  const [cliResult, setCliResult] = useState(null);
  const [configCmds, setConfigCmds] = useState('');
  const [configRunning, setConfigRunning] = useState(false);
  const [configResult, setConfigResult] = useState(null);

  // ── Per-function action cards state ──
  const [actionValues, setActionValuesState] = useState({});
  const [actionRunning, setActionRunning] = useState(null); // key of running action
  const [actionResults, setActionResults] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await settingsAPI.getOLTV2();
        if (data.data) {
          setForm(f => ({
            ...f,
            ...data.data,
            telnet: { ...f.telnet, ...data.data.telnet, password: data.data.telnet?.password ? MASKED : '' },
            ssh: { ...f.ssh, ...data.data.ssh, password: data.data.ssh?.password ? MASKED : '' },
            snmp: { ...f.snmp, ...data.data.snmp, community: data.data.snmp?.community ? MASKED : '' },
          }));
        }
      } catch {
        toast.error('Failed to load OLT V2 settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }));
  const setSub = (group, key, value) => setForm(f => ({ ...f, [group]: { ...f[group], [key]: value } }));
  const setActionValues = (key, field, value) => setActionValuesState(v => ({ ...v, [key]: { ...v[key], [field]: value } }));

  const save = async () => {
    if (!form.ip?.trim()) { toast.error('OLT IP address is required'); return; }
    setSaving(true);
    try {
      const { data } = await settingsAPI.saveOLTV2(form);
      toast.success('OLT V2 settings saved');
      setForm(f => ({
        ...f, ...data.data,
        telnet: { ...f.telnet, ...data.data.telnet },
        ssh: { ...f.ssh, ...data.data.ssh },
        snmp: { ...f.snmp, ...data.data.snmp },
      }));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const runTest = async (kind, call) => {
    setTesting(kind);
    setResults(r => ({ ...r, [kind]: null }));
    try {
      const { data } = await call();
      setResults(r => ({ ...r, [kind]: { ok: true, message: data.message, data: data.data } }));
    } catch (err) {
      setResults(r => ({ ...r, [kind]: { ok: false, message: err.response?.data?.message || err.message } }));
    } finally {
      setTesting(null);
    }
  };

  const ResultBox = ({ kind }) => {
    const r = results[kind];
    if (!r) return null;
    return (
      <div style={{ fontSize: '.8rem', marginTop: 8, color: r.ok ? 'var(--c-059669)' : 'var(--danger)', fontWeight: 600 }}>
        {r.message}
        {r.ok && r.data && (
          <div style={{ fontWeight: 400, color: 'var(--text-muted)', marginTop: 4 }}>
            {Object.entries(r.data).map(([k, v]) => `${k}: ${v}`).join(' · ')}
          </div>
        )}
      </div>
    );
  };

  const runCli = async () => {
    if (!cliCmd.trim()) return;
    setCliRunning(true);
    setCliResult(null);
    try {
      const { data } = await oltV2API.runCommand(cliCmd);
      setCliResult({ ok: true, output: data.data.output });
    } catch (err) {
      setCliResult({ ok: false, message: err.response?.data?.message || err.message });
    } finally {
      setCliRunning(false);
    }
  };

  const runConfig = async () => {
    const commands = configCmds.split('\n').map(s => s.trim()).filter(Boolean);
    if (!commands.length) return;
    setConfigRunning(true);
    setConfigResult(null);
    try {
      const { data } = await oltV2API.sendConfig(commands);
      setConfigResult({ ok: true, output: data.data.outputs.join('\n---\n') });
    } catch (err) {
      setConfigResult({ ok: false, message: err.response?.data?.message || err.message });
    } finally {
      setConfigRunning(false);
    }
  };

  const runAction = async (action) => {
    setActionRunning(action.key);
    setActionResults(r => ({ ...r, [action.key]: null }));
    try {
      const { data } = await action.run(actionValues[action.key] || {});
      setActionResults(r => ({ ...r, [action.key]: { ok: true, data: data.data } }));
    } catch (err) {
      setActionResults(r => ({ ...r, [action.key]: { ok: false, message: err.response?.data?.message || err.message } }));
    } finally {
      setActionRunning(null);
    }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Antenna size={22} /> OLT Info V2.0
          </h1>
          <p className="page-subtitle">
            Pure CLI automation (Telnet or SSH) — independent device, credentials and session from OLT Management, which talks to the OLT's management IP over HTTP.
          </p>
        </div>
        <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? <Loader2 size={14} className="spin" style={{ marginRight: 6 }} /> : <Save size={14} style={{ marginRight: 6 }} />}
          {saving ? 'Saving…' : 'Save Settings'}
        </button>
      </div>

      <div className="stat-card" style={{
        padding: 18, marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        border: form.isEnabled ? '1px solid var(--cb-f59e0b)' : undefined,
      }}>
        <div>
          <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={16} color={form.isEnabled ? 'var(--c-b45309)' : 'var(--text-muted)'} />
            Enable live actions
          </div>
          <div style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginTop: 4 }}>
            Master switch for every fetch/push action below (Telnet, SSH, SNMP) — enforced on the backend,
            not just hidden here. Off by default. Must be saved before it takes effect.
          </div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <input type="checkbox" checked={!!form.isEnabled} onChange={e => set('isEnabled', e.target.checked)} style={{ width: 18, height: 18 }} />
          <span style={{ fontWeight: 600, color: form.isEnabled ? 'var(--c-b45309)' : 'var(--text-muted)' }}>{form.isEnabled ? 'ON' : 'OFF'}</span>
        </label>
      </div>

      <div className="stat-card" style={{ padding: 18, marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 220px', minWidth: 220 }}>
          <FIELD label="OLT IP Address" hint="This integration's own device pointer — same OLT as OLT Management, or a different one.">
            <input className="form-input" value={form.ip} onChange={e => set('ip', e.target.value)} placeholder="e.g. 103.145.9.238" />
          </FIELD>
        </div>
        <div style={{ flex: '1 1 220px', minWidth: 220 }}>
          <FIELD label="CLI Transport" hint="Which one the Fetch/Push actions below actually use — both are configured and testable regardless.">
            <select className="form-input" value={form.cliTransport} onChange={e => set('cliTransport', e.target.value)}>
              <option value="telnet">Telnet</option>
              <option value="ssh">SSH</option>
            </select>
          </FIELD>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 16 }}>
        {/* Telnet login */}
        <div className="stat-card" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700 }}><Terminal size={16} /> Telnet CLI</div>
          <FIELD label="Port"><input className="form-input" type="number" value={form.telnet.port} onChange={e => setSub('telnet', 'port', +e.target.value)} /></FIELD>
          <FIELD label="Username"><input className="form-input" value={form.telnet.username} onChange={e => setSub('telnet', 'username', e.target.value)} /></FIELD>
          <FIELD label="Password">
            <input className="form-input" type="password" value={form.telnet.password}
              onFocus={() => form.telnet.password === MASKED && setSub('telnet', 'password', '')}
              onChange={e => setSub('telnet', 'password', e.target.value)} />
          </FIELD>
          <button type="button" className="btn btn-secondary btn-sm" disabled={testing === 'telnet'}
            onClick={() => runTest('telnet', settingsAPI.testOLTV2Telnet)}>
            {testing === 'telnet' ? <Loader2 size={13} className="spin" style={{ marginRight: 5 }} /> : <RefreshCw size={13} style={{ marginRight: 5 }} />}
            Test Telnet
          </button>
          <ResultBox kind="telnet" />
        </div>

        {/* SSH login */}
        <div className="stat-card" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700 }}><Lock size={16} /> SSH CLI</div>
          <FIELD label="Port"><input className="form-input" type="number" value={form.ssh.port} onChange={e => setSub('ssh', 'port', +e.target.value)} /></FIELD>
          <FIELD label="Username"><input className="form-input" value={form.ssh.username} onChange={e => setSub('ssh', 'username', e.target.value)} /></FIELD>
          <FIELD label="Password">
            <input className="form-input" type="password" value={form.ssh.password}
              onFocus={() => form.ssh.password === MASKED && setSub('ssh', 'password', '')}
              onChange={e => setSub('ssh', 'password', e.target.value)} />
          </FIELD>
          <button type="button" className="btn btn-secondary btn-sm" disabled={testing === 'ssh'}
            onClick={() => runTest('ssh', settingsAPI.testOLTV2Ssh)}>
            {testing === 'ssh' ? <Loader2 size={13} className="spin" style={{ marginRight: 5 }} /> : <RefreshCw size={13} style={{ marginRight: 5 }} />}
            Test SSH
          </button>
          <ResultBox kind="ssh" />
        </div>

        {/* SNMP */}
        <div className="stat-card" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700 }}><Radio size={16} /> SNMP</div>
          <FIELD label="Port"><input className="form-input" type="number" value={form.snmp.port} onChange={e => setSub('snmp', 'port', +e.target.value)} /></FIELD>
          <FIELD label="Version">
            <select className="form-input" value={form.snmp.version} onChange={e => setSub('snmp', 'version', e.target.value)}>
              <option value="1">v1</option>
              <option value="2c">v2c</option>
            </select>
          </FIELD>
          <FIELD label="Community String">
            <input className="form-input" value={form.snmp.community}
              onFocus={() => form.snmp.community === MASKED && setSub('snmp', 'community', '')}
              onChange={e => setSub('snmp', 'community', e.target.value)} placeholder="e.g. public" />
          </FIELD>
          <button type="button" className="btn btn-secondary btn-sm" disabled={testing === 'snmp'}
            onClick={() => runTest('snmp', settingsAPI.testOLTV2Snmp)}>
            {testing === 'snmp' ? <Loader2 size={13} className="spin" style={{ marginRight: 5 }} /> : <RefreshCw size={13} style={{ marginRight: 5 }} />}
            Test SNMP
          </button>
          <ResultBox kind="snmp" />
        </div>
      </div>

      {!form.isEnabled && (
        <div className="stat-card" style={{ padding: 14, marginBottom: 16, fontSize: '.85rem', color: 'var(--text-muted)' }}>
          Live actions are hidden — turn on "Enable live actions" above and Save to reveal them.
        </div>
      )}

      {form.isEnabled && (
        <>
          {/* Raw CLI — the actual tool for command-tree discovery (try '?' or 'help' first) */}
          <div className="stat-card" style={{ padding: 18, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontWeight: 700 }}>Run Raw CLI Command <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>— via {form.cliTransport === 'ssh' ? 'SSH' : 'Telnet'}</span></div>
            <div style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>
              Start here — try <code>?</code> or <code>help</code> to see the device's real command tree before using any of the function cards below.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="form-input" placeholder="e.g. ?" value={cliCmd} onChange={e => setCliCmd(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && runCli()} />
              <button type="button" className="btn btn-secondary btn-sm" disabled={cliRunning} onClick={runCli}>
                {cliRunning ? <Loader2 size={13} className="spin" style={{ marginRight: 5 }} /> : <Play size={13} style={{ marginRight: 5 }} />}
                Run
              </button>
            </div>
            {cliResult && (
              <pre style={{ fontSize: '.78rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 240, overflow: 'auto', background: 'var(--bg-secondary, #f8fafc)', padding: 10, borderRadius: 6, color: cliResult.ok ? 'var(--text)' : 'var(--danger)' }}>
                {cliResult.ok ? cliResult.output : cliResult.message}
              </pre>
            )}
          </div>

          {/* Raw config sequence — multi-command push */}
          <div className="stat-card" style={{ padding: 18, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontWeight: 700 }}>Send Raw Config Sequence</div>
            <div style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>One command per line, sent in order over one session.</div>
            <textarea className="form-input" rows={3} value={configCmds} onChange={e => setConfigCmds(e.target.value)}
              placeholder={'enable\nconfigure terminal\n...'} />
            <div>
              <button type="button" className="btn btn-danger btn-sm" disabled={configRunning} onClick={runConfig}>
                {configRunning ? <Loader2 size={13} className="spin" style={{ marginRight: 5 }} /> : <Upload size={13} style={{ marginRight: 5 }} />}
                Send
              </button>
            </div>
            {configResult && (
              <pre style={{ fontSize: '.78rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 240, overflow: 'auto', background: 'var(--bg-secondary, #f8fafc)', padding: 10, borderRadius: 6, color: configResult.ok ? 'var(--text)' : 'var(--danger)' }}>
                {configResult.ok ? configResult.output : configResult.message}
              </pre>
            )}
          </div>

          {/* Mirrored fetch/push function cards */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, marginBottom: 8 }}>
            <Download size={16} /> Fetch
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 16 }}>
            {FETCH_ACTIONS.map(a => (
              <ActionCard key={a.key} action={a} values={actionValues[a.key] || {}} setValues={setActionValues}
                running={actionRunning === a.key} onRun={() => runAction(a)} result={actionResults[a.key]} />
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, marginBottom: 8 }}>
            <Upload size={16} /> Push
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            {PUSH_ACTIONS.map(a => (
              <ActionCard key={a.key} action={a} values={actionValues[a.key] || {}} setValues={setActionValues}
                running={actionRunning === a.key} onRun={() => runAction(a)} result={actionResults[a.key]} danger />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
